import { CAPABILITY, selMessage } from "@selector/shared";
import { equals } from "ramda";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CalloutIntent, DialogComponent, HStack, Modal, TriggerButton, TriggerButtonIntent, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import { createSelectionModel } from "@/app-utils/selection-model.ts";
import type { Capability, GenomeConstraints, InputTableQuerySpec } from "@/graphql.ts";
import { checkCapability } from "@/modules/users/utils/user.ts";
import { CreateVirtualGenomeDialog } from "@/modules/workflow/components/parameters/CreateVirtualGenomeDialog.tsx";
import { createUniqueKeyFromTableSpec, selectTableRows } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";

interface GenomeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedGenomes: number[]) => void;
  initialSelection: number[];
  multiSelection?: boolean;
  genomeConstraints?: GenomeConstraints;
}

export const GenomeSelectionModal = ({
  isOpen,
  onClose,
  onSave,
  initialSelection,
  multiSelection,
  genomeConstraints,
}: GenomeSelectionModalProps) => {
  const { overlayProps, state } = useOverlay({ isOpen });
  const userCapabilities = useSelector<IAppState, Capability[] | null>(
    (state) => state.auth.session?.capabilities?.filter((c) => c != null) ?? null,
    equals,
  );
  const isAllowedToCreateVirtualGenome = useMemo(
    () => checkCapability(userCapabilities, CAPABILITY.CREATE_VIRTUAL_GENOME),
    [userCapabilities],
  );

  const [isSaving, setIsSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedGenomeIds, setSelectedGenomeIds] = useState<number[]>(initialSelection);

  const dispatch = useDispatch<ReduxDispatch>();

  const tableSpec: InputTableQuerySpec | undefined = useMemo(
    () =>
      ({
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: DATATABLE_PARENT_TYPE.ORGANISM,
        resultType: ENTITY_TYPE.ORGANISM,
        restrictions: {
          organismTypes: genomeConstraints?.allowedTypes,
        },
      }) as InputTableQuerySpec,
    [genomeConstraints?.allowedTypes],
  );

  const tableUniqueKey = useMemo(() => createUniqueKeyFromTableSpec(tableSpec), [tableSpec]);
  const tableSelectionModel = createSelectionModel(selectedGenomeIds);

  const createVirtualGenomedisabledTooltip = useMemo(() => {
    if (selectedGenomeIds.length < 2) {
      return "You are not allowed to create a virtual genome with only a single genome component.";
    } else if (selectedGenomeIds.length > 100) {
      return "You are not allowed to create a virtual genome with more than 100 genome components.";
    }
    return undefined;
  }, [selectedGenomeIds]);

  return (
    <>
      <Modal state={state} {...overlayProps}>
        <DialogComponent
          fullscreen
          title={multiSelection ? "Select Genomes" : "Select Genome"}
          mainButton={{
            label: multiSelection ? "Select Genomes" : "Select Genome",
            onPress: () => {
              setIsSaving(true);
              onSave(selectedGenomeIds);
              setIsSaving(false);
              onClose();
            },
            isDisabled: isSaving || !multiSelection ? tableSelectionModel.selectedIds.length > 1 : false,
            isLoading: isSaving,
          }}
          actions={[<ColumnSelectionButton key="column-selection-button-placeholder" portalContext={tableUniqueKey} />]}
          infoComponent={
            <HStack>
              {isAllowedToCreateVirtualGenome && (
                <TriggerButton
                  icon={"add"}
                  intent={TriggerButtonIntent.Secondary}
                  onPress={() => setShowCreateDialog(true)}
                  label={"New Virtual Genome"}
                  isDisabled={createVirtualGenomedisabledTooltip}
                  showLabel
                />
              )}
              <TriggerButton
                icon={"restart_alt"}
                intent={TriggerButtonIntent.Secondary}
                onPress={() => dispatch(selectTableRows(tableSpec, [], false, false))}
                label={"Reset Selection"}
                showLabel
              />
            </HStack>
          }
          closeFn={() => {
            setIsSaving(false);
            onClose();
          }}
          errorCallout={
            !multiSelection && tableSelectionModel.selectedIds.length > 1
              ? { intent: CalloutIntent.Warning, message: selMessage({ id: "error.single.genome" }) }
              : undefined
          }
          contentStyle={{ display: "grid", padding: 0, borderTop: "1px solid var(--ui-border)" }}
        >
          <VStack style={{ width: "100%", height: "100%" }}>
            {/* Ideally, sort table to have the selected ones on top */}
            {/* @ts-expect-error Reported missing properties of this component are actually not required */}
            <DataTableContainer
              key={tableUniqueKey}
              tableSpec={tableSpec}
              gridContext={{ tableSpec }}
              noRowsText="No genomes found"
              onSelectionModelChanged={(selected) => {
                setSelectedGenomeIds(selected.selectedIds as number[]);
              }}
              tableInitializationOptions={{
                initialSelectionModel: tableSelectionModel,
              }}
            />
          </VStack>
        </DialogComponent>
      </Modal>
      {/* We need to force unmount the dialog*/}
      {showCreateDialog && (
        <CreateVirtualGenomeDialog
          isOpen={showCreateDialog}
          onClose={(virtualGenome) => {
            if (virtualGenome) {
              setSelectedGenomeIds([virtualGenome.id]);
              // overwrite table selection
              dispatch(selectTableRows(tableSpec, [virtualGenome.id], true, false));
            }
            setShowCreateDialog(false);
          }}
          selectedGenomes={selectedGenomeIds}
        />
      )}
    </>
  );
};
